import { __assign, __rest } from "tslib";
import { useState, useEffect, useRef, cloneElement } from 'react';
import { render } from 'react-universal-interface';
import useLatest from './useLatest';

var noop = function noop() {};

var useScratch = function useScratch(params) {
  if (params === void 0) {
    params = {};
  }

  var disabled = params.disabled;
  var paramsRef = useLatest(params);

  var _a = useState({
    isScratching: false
  }),
      state = _a[0],
      setState = _a[1];

  var refState = useRef(state);
  var refScratching = useRef(false);
  var refAnimationFrame = useRef(null);

  var _b = useState(null),
      el = _b[0],
      setEl = _b[1];

  useEffect(function () {
    if (disabled) return;
    if (!el) return;

    var onMoveEvent = function onMoveEvent(docX, docY) {
      cancelAnimationFrame(refAnimationFrame.current);
      refAnimationFrame.current = requestAnimationFrame(function () {
        var _a = el.getBoundingClientRect(),
            left = _a.left,
            top = _a.top;

        var elX = left + window.scrollX;
        var elY = top + window.scrollY;
        var x = docX - elX;
        var y = docY - elY;
        setState(function (oldState) {
          var newState = __assign(__assign({}, oldState), {
            dx: x - (oldState.x || 0),
            dy: y - (oldState.y || 0),
            end: Date.now(),
            isScratching: true
          });

          refState.current = newState;
          (paramsRef.current.onScratch || noop)(newState);
          return newState;
        });
      });
    };

    var onMouseMove = function onMouseMove(event) {
      onMoveEvent(event.pageX, event.pageY);
    };

    var onTouchMove = function onTouchMove(event) {
      onMoveEvent(event.changedTouches[0].pageX, event.changedTouches[0].pageY);
    };

    var onMouseUp;
    var onTouchEnd;

    var stopScratching = function stopScratching() {
      if (!refScratching.current) return;
      refScratching.current = false;
      refState.current = __assign(__assign({}, refState.current), {
        isScratching: false
      });
      (paramsRef.current.onScratchEnd || noop)(refState.current);
      setState({
        isScratching: false
      });
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchend', onTouchEnd);
    };

    onMouseUp = stopScratching;
    onTouchEnd = stopScratching;

    var startScratching = function startScratching(docX, docY) {
      if (!refScratching.current) return;

      var _a = el.getBoundingClientRect(),
          left = _a.left,
          top = _a.top;

      var elX = left + window.scrollX;
      var elY = top + window.scrollY;
      var x = docX - elX;
      var y = docY - elY;
      var time = Date.now();
      var newState = {
        isScratching: true,
        start: time,
        end: time,
        docX: docX,
        docY: docY,
        x: x,
        y: y,
        dx: 0,
        dy: 0,
        elH: el.offsetHeight,
        elW: el.offsetWidth,
        elX: elX,
        elY: elY
      };
      refState.current = newState;
      (paramsRef.current.onScratchStart || noop)(newState);
      setState(newState);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('touchend', onTouchEnd);
    };

    var onMouseDown = function onMouseDown(event) {
      refScratching.current = true;
      startScratching(event.pageX, event.pageY);
    };

    var onTouchStart = function onTouchStart(event) {
      refScratching.current = true;
      startScratching(event.changedTouches[0].pageX, event.changedTouches[0].pageY);
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('touchstart', onTouchStart);
    return function () {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchend', onTouchEnd);
      if (refAnimationFrame.current) cancelAnimationFrame(refAnimationFrame.current);
      refAnimationFrame.current = null;
      refScratching.current = false;
      refState.current = {
        isScratching: false
      };
      setState(refState.current);
    };
  }, [el, disabled, paramsRef]);
  return [setEl, state];
};

export var ScratchSensor = function ScratchSensor(props) {
  var children = props.children,
      params = __rest(props, ["children"]);

  var _a = useScratch(params),
      _ref = _a[0],
      state = _a[1];

  var element = render(props, state);
  return cloneElement(element, __assign(__assign({}, element.props), {
    ref: function ref(el) {
      if (element.props.ref) {
        if (typeof element.props.ref === 'object') element.props.ref.current = el;
        if (typeof element.props.ref === 'function') element.props.ref(el);
      }

      _ref(el);
    }
  }));
};
export default useScratch;